import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const ROLES = [
  { value: 'observer', label: 'Наблюдатель', color: 'default', description: 'Только просмотр данных' },
  { value: 'moderator', label: 'Модератор', color: 'info', description: 'Редактирование данных и диалоги в чатах' },
  { value: 'admin', label: 'Администратор', color: 'error', description: 'Полный доступ, включая управление операторами' }
];

const getRoleMeta = (role) => ROLES.find((r) => r.value === role) || ROLES[0];

const AdminUsersPage = () => {
  const { user: currentUser } = useAuth();

  // Состояние данных
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  // Диалог создания/редактирования
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({ username: '', password: '', role: 'observer' });
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Диалог удаления
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Уведомления
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await apiService.adminUsers.getAll();
      if (response.data.success) {
        setAdmins(response.data.admins);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей админ-панели:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Не удалось загрузить список пользователей',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  // Открытие диалога создания
  const handleOpenCreateDialog = () => {
    setEditingAdmin(null);
    setFormData({ username: '', password: '', role: 'observer' });
    setFormError('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  // Открытие диалога редактирования
  const handleOpenEditDialog = (admin) => {
    setEditingAdmin(admin);
    setFormData({ username: admin.username, password: '', role: admin.role });
    setFormError('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAdmin(null);
    setFormError('');
  };

  // Сохранение (создание или обновление)
  const handleSave = async () => {
    if (!formData.username.trim()) {
      setFormError('Укажите имя оператора');
      return;
    }
    if (!editingAdmin && !formData.password) {
      setFormError('Укажите пароль');
      return;
    }
    if (formData.password && formData.password.length < 6) {
      setFormError('Пароль должен содержать не менее 6 символов');
      return;
    }

    try {
      setActionLoading(true);
      setFormError('');

      if (editingAdmin) {
        const payload = { username: formData.username.trim(), role: formData.role };
        // Пароль отправляем только если он введен
        if (formData.password) payload.password = formData.password;

        const response = await apiService.adminUsers.update(editingAdmin.id, payload);
        if (response.data.success) {
          setSnackbar({ open: true, message: 'оператор обновлен', severity: 'success' });
          handleCloseDialog();
          fetchAdmins();
        }
      } else {
        const response = await apiService.adminUsers.create({
          username: formData.username.trim(),
          password: formData.password,
          role: formData.role
        });
        if (response.data.success) {
          setSnackbar({ open: true, message: 'оператор создан', severity: 'success' });
          handleCloseDialog();
          fetchAdmins();
        }
      }
    } catch (error) {
      console.error('Ошибка сохранения оператора админ-панели:', error);
      setFormError(error.response?.data?.message || 'Не удалось сохранить оператора');
    } finally {
      setActionLoading(false);
    }
  };

  // Удаление
  const handleOpenDeleteDialog = (admin) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
  };

  const handleDelete = async () => {
    if (!adminToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await apiService.adminUsers.delete(adminToDelete.id);
      if (response.data.success) {
        setAdmins(admins.filter((a) => a.id !== adminToDelete.id));
        setSnackbar({ open: true, message: 'оператор удален', severity: 'success' });
        handleCloseDeleteDialog();
      }
    } catch (error) {
      console.error('Ошибка удаления оператора админ-панели:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Не удалось удалить оператора',
        severity: 'error'
      });
      handleCloseDeleteDialog();
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Нет данных';
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'Нет данных' : date.toLocaleString('ru-RU');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Операторы панели управления
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Учётные записи операторов с доступом к панели управления
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          Добавить оператора
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Имя оператора</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Роль</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Последний вход</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Создан</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">Пользователи не найдены</Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => {
                const roleMeta = getRoleMeta(admin.role);
                const isSelf = String(admin.id) === String(currentUser?.id);
                return (
                  <TableRow key={admin.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {admin.username}
                        {isSelf && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            (это вы)
                          </Typography>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={roleMeta.label}
                        color={roleMeta.color}
                        size="small"
                        variant={admin.role === 'observer' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(admin.lastLogin)}</TableCell>
                    <TableCell>{formatDate(admin.createdAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Редактировать">
                        <IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(admin)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isSelf ? 'Нельзя удалить собственную учетную запись' : 'Удалить'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={isSelf}
                            onClick={() => handleOpenDeleteDialog(admin)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания/редактирования */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAdmin ? 'Редактировать оператора' : 'Новый оператор админ-панели'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            margin="normal"
            fullWidth
            required
            label="Логин оператора"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            autoFocus
          />
          <TextField
            margin="normal"
            fullWidth
            required={!editingAdmin}
            label="Пароль"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            helperText={editingAdmin ? 'Оставьте пустым, чтобы не менять пароль' : 'Минимум 6 символов'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            margin="normal"
            fullWidth
            required
            select
            label="Роль"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            {ROLES.map((role) => (
              <MenuItem key={role.value} value={role.value}>
                <Box>
                  <Typography variant="body2">{role.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {role.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading && <CircularProgress size={20} color="inherit" />}
          >
            {editingAdmin ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог удаления */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Удалить оператора?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить оператора «{adminToDelete?.username}» ({getRoleMeta(adminToDelete?.role).label.toLowerCase()})?
            Это действие невозможно отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteLoading}>
            Отмена
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомление */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersPage;
